
import Vue from "vue";
import {
  createRegionalFreePeriodCampaign,
  deactivateRegionalFreePeriodCampaign,
  getCurrentAdminRole,
  getRegionalFreePeriodCampaigns,
  RegionalFreePeriodCampaign,
  RegionalFreePeriodCampaignInput,
  RegionalFreePeriodRequestError,
  RegionalFreePeriodRevision,
  updateRegionalFreePeriodCampaign
} from "../../api";

const emptyForm = (): RegionalFreePeriodCampaignInput => ({
  province: "BC",
  campaignName: "",
  startDate: "",
  endDate: "",
  placementFeeWaived: true,
  tokensWaived: true
});

/** Preferred Office Promotions routes if/when T2-6869 ships. */
const OFFICE_PROMOTIONS_CANDIDATES = [
  { path: "/system/office-promotions", label: "Office Promotions" },
  { path: "/system/office-promotion", label: "Office Promotions" },
  { path: "/admin/office-promotions", label: "Office Promotions" }
];

export default Vue.extend({
  data() {
    return {
      loading: false,
      saving: false,
      error: "",
      success: "",
      dialogError: "",
      campaigns: [] as RegionalFreePeriodCampaign[],
      dialog: false,
      deactivateDialog: false,
      formValid: false,
      editingId: null as number | null,
      pendingDeactivate: null as RegionalFreePeriodCampaign | null,
      form: emptyForm(),
      officePromotionsRoute: null as string | null,
      officePromotionsLabel: "Office Promotions",
      statusHint:
        "Scheduled = future start; Active = currently providing benefits; Expired = past end date; Off/Inactive = turned off (history preserved).",
      hints: {
        campaignName:
          "Internal label shown in this list and in admin history. Keep it clear for Marketing/Ops (e.g. “BC Aug–Sep 2026”).",
        region:
          "Province or state where offices qualify. Cannot be changed when revising — create a new campaign for a different region.",
        startDate:
          "Inclusive first activity date that can qualify (shift completion or resume view) — not the office signup date.",
        endDate:
          "Inclusive last activity date that can qualify. Must be on or after the start date.",
        placementFees:
          "When on, qualifying completed shifts in this region during the dates have placement fees waived.",
        tokens:
          "When on, qualifying resume views by offices in this region during the dates do not consume Job Board tokens.",
        onOff:
          "Creating a campaign turns it On for the date range. Use Turn Off on a row to stop future benefits while keeping history. Editing saves a new revision."
      },
      regions: [
        "AB",
        "BC",
        "MB",
        "NB",
        "NL",
        "NS",
        "NT",
        "NU",
        "ON",
        "PE",
        "QC",
        "SK",
        "YT",
        "AK",
        "AL",
        "AR",
        "AZ",
        "CA",
        "CO",
        "CT",
        "DC",
        "DE",
        "FL",
        "GA",
        "HI",
        "IA",
        "ID",
        "IL",
        "IN",
        "KS",
        "KY",
        "LA",
        "MA",
        "MD",
        "ME",
        "MI",
        "MN",
        "MO",
        "MS",
        "MT",
        "NC",
        "ND",
        "NE",
        "NH",
        "NJ",
        "NM",
        "NV",
        "NY",
        "OH",
        "OK",
        "OR",
        "PA",
        "RI",
        "SC",
        "SD",
        "TN",
        "TX",
        "UT",
        "VA",
        "VT",
        "WA",
        "WI",
        "WV",
        "WY"
      ],
      headers: [
        { text: "Campaign", value: "campaignName" },
        { text: "Region", value: "region" },
        { text: "Campaign dates", value: "dates", sortable: false },
        { text: "Benefits", value: "benefits", sortable: false },
        { text: "Status", value: "status" },
        { text: "Effective revision", value: "effective", sortable: false },
        { text: "Actions", value: "actions", sortable: false, align: "end" },
        { text: "", value: "data-table-expand", sortable: false }
      ],
      rules: {
        required: (value: string) => !!value || "Required",
        maxName: (value: string) =>
          !value || value.length <= 120 || "Maximum 120 characters"
      }
    };
  },
  computed: {
    hasBenefit(): boolean {
      return this.form.placementFeeWaived || this.form.tokensWaived;
    }
  },
  async created() {
    this.resolveOfficePromotionsLink();
    try {
      const admin = await getCurrentAdminRole();
      if (!admin.isDeveloper) {
        this.$router.replace({ name: "home" });
        return;
      }
      await this.load();
    } catch (error) {
      this.error = this.errorMessage(error);
    }
  },
  methods: {
    resolveOfficePromotionsLink() {
      const routes = this.$router.getRoutes
        ? this.$router.getRoutes()
        : (this.$router as any).options?.routes || [];
      const paths = new Set(
        (routes as Array<{ path?: string; name?: string }>).map(r => r.path || "")
      );
      for (const candidate of OFFICE_PROMOTIONS_CANDIDATES) {
        if (paths.has(candidate.path)) {
          this.officePromotionsRoute = candidate.path;
          this.officePromotionsLabel = candidate.label;
          return;
        }
      }
      this.officePromotionsRoute = null;
    },
    errorMessage(error: unknown): string {
      return error instanceof Error
        ? error.message
        : "Unable to complete the request";
    },
    dateOrderRule(value: string): boolean | string {
      return (
        !value ||
        !this.form.startDate ||
        value >= this.form.startDate ||
        "End date must be on or after start date"
      );
    },
    async load() {
      this.loading = true;
      try {
        this.campaigns = await getRegionalFreePeriodCampaigns();
      } catch (error) {
        this.error = this.errorMessage(error);
      } finally {
        this.loading = false;
      }
    },
    statusColor(status: string): string {
      if (status === "active") return "green";
      if (status === "scheduled") return "blue";
      return "grey";
    },
    visibleConfigurations(
      item: RegionalFreePeriodCampaign
    ): Array<{
      label: string;
      revision: RegionalFreePeriodRevision;
    }> {
      const configurations: Array<{
        label: string;
        revision: RegionalFreePeriodRevision;
      }> = [];
      if (item.currentRevision) {
        configurations.push({
          label: "Current",
          revision: item.currentRevision
        });
      }
      if (item.pendingRevision) {
        configurations.push({
          label: "Pending",
          revision: item.pendingRevision
        });
      }
      if (!configurations.length) {
        configurations.push({ label: "Latest", revision: item.latestRevision });
      }
      return configurations;
    },
    revisionLabel(
      item: RegionalFreePeriodCampaign,
      revision: RegionalFreePeriodRevision
    ): string {
      if (item.currentRevision && revision.id === item.currentRevision.id) {
        return "Current";
      }
      if (item.pendingRevision && revision.id === item.pendingRevision.id) {
        return "Pending";
      }
      if (revision.id !== item.latestRevisionId) {
        return "Superseded";
      }
      return revision.status.charAt(0).toUpperCase() + revision.status.slice(1);
    },
    revisionColor(
      item: RegionalFreePeriodCampaign,
      revision: RegionalFreePeriodRevision
    ): string {
      const label = this.revisionLabel(item, revision);
      if (label === "Current") return "green";
      if (label === "Pending") return "blue";
      return "grey";
    },
    canModify(item: RegionalFreePeriodCampaign): boolean {
      return item.editableRevisionId !== null;
    },
    openCreate() {
      this.editingId = null;
      this.form = emptyForm();
      this.dialogError = "";
      this.dialog = true;
      this.$nextTick(() => (this.$refs.form as any)?.resetValidation?.());
    },
    openEdit(item: RegionalFreePeriodCampaign) {
      const revision = item.latestRevision;
      this.editingId = item.editableRevisionId;
      this.form = {
        province: revision.province,
        campaignName: revision.campaignName,
        startDate: revision.startDate,
        endDate: revision.endDate,
        placementFeeWaived: revision.placementFeeWaived,
        tokensWaived: revision.tokensWaived
      };
      this.dialogError = "";
      this.dialog = true;
      this.$nextTick(() => (this.$refs.form as any)?.resetValidation?.());
    },
    closeDialog() {
      this.dialog = false;
      this.dialogError = "";
    },
    async save() {
      if (this.saving) return;
      if (!(this.$refs.form as any)?.validate() || !this.hasBenefit) return;
      this.saving = true;
      this.error = "";
      this.dialogError = "";
      try {
        if (this.editingId) {
          const result = await updateRegionalFreePeriodCampaign(
            this.editingId,
            this.form
          );
          this.success = result.revisionCreated
            ? "Campaign revision saved."
            : result.idempotentReplay
            ? "This campaign change was already saved."
            : "No changes to save; the campaign remains unchanged.";
        } else {
          await createRegionalFreePeriodCampaign(this.form);
          this.success = "Campaign created.";
        }
        this.dialog = false;
        await this.load();
      } catch (error) {
        const requestError = error as RegionalFreePeriodRequestError;
        this.dialogError = this.errorMessage(error);
        if (requestError.domainCode === "CAMPAIGN_STALE") {
          await this.load();
        }
      } finally {
        this.saving = false;
      }
    },
    confirmDeactivate(item: RegionalFreePeriodCampaign) {
      this.pendingDeactivate = item;
      this.deactivateDialog = true;
    },
    async deactivate() {
      if (!this.pendingDeactivate) return;
      this.saving = true;
      this.error = "";
      try {
        if (this.pendingDeactivate.editableRevisionId === null) {
          throw new Error("This campaign no longer has an editable revision.");
        }
        await deactivateRegionalFreePeriodCampaign(
          this.pendingDeactivate.editableRevisionId
        );
        this.success =
          "Campaign turned off for the next local calendar day. Historical eligibility remains unchanged.";
        this.deactivateDialog = false;
        this.pendingDeactivate = null;
        await this.load();
      } catch (error) {
        this.error = this.errorMessage(error);
      } finally {
        this.saving = false;
      }
    }
  }
});
